// Core Modules
import { ChangeDetectionStrategy, Component, computed, ElementRef, HostListener, effect, inject, OnDestroy, signal, viewChild } from '@angular/core';

// External Modules
import { Subscription } from 'rxjs';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';

// Services
import { IgniteAssistantService } from './ignite-assistant.service';

// Pipes
import { MarkdownPipe } from '@shared/pipes/markdown.pipe';

// Data
import {
  ASSISTANT_ERROR_MESSAGE,
  ASSISTANT_GREETING,
  ASSISTANT_HELP_CATEGORIES,
  ASSISTANT_HISTORY_PAGE_SIZE,
  ASSISTANT_HISTORY_SKELETON_ROWS,
  ASSISTANT_HISTORY_TITLE_MAX_LENGTH,
  ASSISTANT_INPUT_PLACEHOLDER,
  ASSISTANT_MAX_INPUT_LENGTH,
  ASSISTANT_PRESET_QUESTIONS,
  ASSISTANT_WAITING_PLACEHOLDER,
} from './ignite-assistant.data';

// Models
import { AssistantHelpCategory, AssistantMessage, ConversationSummary, ConversationTurn } from './ignite-assistant.model';

type AssistantView = 'chat' | 'history' | 'help';

/**
 * Floating "Ignite Assistant" chat widget. Sends each turn to the assistant API
 * (`POST /chat`) and reads/writes saved conversation history through the
 * backend chat-history API. Rendered globally over the authenticated app.
 */
@Component({
  selector: 'app-ignite-assistant',
  standalone: true,
  imports: [MarkdownPipe, NgbCollapseModule],
  templateUrl: './ignite-assistant.component.html',
  styleUrl: './ignite-assistant.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IgniteAssistantComponent implements OnDestroy {
  // Injectors
  private readonly assistantService = inject(IgniteAssistantService);

  // View Children
  private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');
  private readonly moreButton = viewChild<ElementRef<HTMLButtonElement>>('moreButton');
  private readonly firstMenuItem = viewChild<ElementRef<HTMLButtonElement>>('firstMenuItem');
  private readonly chatInput = viewChild<ElementRef<HTMLTextAreaElement>>('chatInput');

  // Private Members
  private pendingRequest?: Subscription;
  private pendingHistoryRequest?: Subscription;
  private pendingOpenRequest?: Subscription;
  private historyPageNum = 0;
  private readonly activeId = signal<string | null>(null);
  private readonly firstHelpCategory = ASSISTANT_HELP_CATEGORIES[0]?.category ?? null;

  // View State
  public readonly open = signal(false);
  public readonly expanded = signal(false);
  public readonly menuOpen = signal(false);
  public readonly view = signal<AssistantView>('chat');
  public readonly input = signal('');
  public readonly typing = signal(false);
  public readonly messages = signal<AssistantMessage[]>([ASSISTANT_GREETING]);
  public readonly conversations = signal<ConversationSummary[]>([]);
  public readonly historyLoading = signal(false);
  /** True only while page 1 is in flight (first open or a refresh) — gates the skeleton, keeping "Load more" (page 2+) from hiding already-loaded rows. */
  public readonly historyRefreshing = signal(false);
  public readonly historyError = signal(false);
  /** Server-side conversation count as of the last fetch, kept in sync with local deletes so historyHasNext never goes stale. */
  private readonly historyTotalCount = signal(0);
  public readonly openConversationError = signal(false);
  public readonly deleteConversationError = signal(false);
  public readonly feedbackErrorTurnId = signal<number | null>(null);
  public readonly searchQuery = signal('');
  public readonly expandedCategory = signal<string | null>(this.firstHelpCategory);

  // Derived State
  public readonly chatView = computed(() => this.view() === 'chat');
  public readonly historyView = computed(() => this.view() === 'history');
  public readonly helpView = computed(() => this.view() === 'help');
  public readonly showSuggestions = computed(() => this.messages().length <= 1 && !this.typing());
  // Swap the composer placeholder to a waiting message while a reply is in flight.
  public readonly inputPlaceholder = computed(() => (this.typing() ? ASSISTANT_WAITING_PLACEHOLDER : ASSISTANT_INPUT_PLACEHOLDER));
  public readonly noConversations = computed(() => this.conversations().length === 0);
  /** True while the server has conversations beyond what's currently displayed. Derived (not a stale flag set once per fetch) so a delete is reflected immediately. */
  public readonly historyHasNext = computed(() => this.conversations().length < this.historyTotalCount());
  public readonly presetQuestions = ASSISTANT_PRESET_QUESTIONS;
  public readonly helpCategories = ASSISTANT_HELP_CATEGORIES;
  public readonly maxInputLength = ASSISTANT_MAX_INPUT_LENGTH;
  public readonly skeletonRows = ASSISTANT_HISTORY_SKELETON_ROWS;
  // Reveal the character counter only as the user approaches the limit.
  public readonly showCharCount = computed(() => this.input().length >= this.maxInputLength - 50);
  // Narrows help questions to those matching the search query; categories with no matches are dropped.
  public readonly filteredCategories = computed<readonly AssistantHelpCategory[]>(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return this.helpCategories;
    return this.helpCategories.map((group) => ({ ...group, questions: group.questions.filter((question) => question.toLowerCase().includes(query)) })).filter((group) => group.questions.length > 0);
  });

  constructor() {
    // Keep the transcript pinned to the newest entry as messages or the typing
    // indicator change. Deferred so the DOM has painted the new content first.
    effect(() => {
      this.messages();
      this.typing();
      this.view();
      this.open();
      const element = this.scrollContainer()?.nativeElement;
      if (element) setTimeout(() => (element.scrollTop = element.scrollHeight));
    });

    // Move focus to the first menu item when the menu opens.
    effect(() => {
      if (this.menuOpen()) {
        const item = this.firstMenuItem()?.nativeElement;
        if (item) setTimeout(() => item.focus());
      }
    });

    // Reset the textarea's grown height whenever the input is cleared (send, new chat,
    // opening/removing a conversation) — none of those go through the native `input`
    // event that normally drives adjustTextareaHeight().
    effect(() => {
      if (this.input() === '') {
        const element = this.chatInput()?.nativeElement;
        if (element) element.style.height = 'auto';
      }
    });
  }

  public toggleOpen(): void {
    this.open.update((value) => !value);
  }

  /** Toggle the larger, top-anchored reading layout so long replies need less scrolling. */
  public toggleExpand(): void {
    this.expanded.update((value) => !value);
  }

  /** Toggle the header overflow menu (New chat / Chat history / Help). */
  public toggleMenu(): void {
    this.menuOpen.update((value) => !value);
  }

  public closeMenu(): void {
    this.menuOpen.set(false);
  }

  public closeChat(): void {
    this.open.set(false);
  }

  public showHistory(): void {
    this.view.set('history');
    this.menuOpen.set(false);
    this.openConversationError.set(false);
    this.deleteConversationError.set(false);
    this.loadHistoryPage(1);
  }

  public showHelp(): void {
    this.view.set('help');
    this.menuOpen.set(false);
    this.searchQuery.set('');
    this.expandedCategory.set(this.firstHelpCategory);
  }

  /** Expand a category, or collapse it if it's already the open one. */
  public toggleCategory(category: string): void {
    this.expandedCategory.set(this.expandedCategory() === category ? null : category);
  }

  /** A category counts as open if it's the active accordion entry, or if the user is searching (every matching category shows expanded). */
  public isCategoryOpen(category: string): boolean {
    return this.searchQuery() ? true : this.expandedCategory() === category;
  }

  public backToChat(): void {
    this.view.set('chat');
  }

  /** Reset to a fresh conversation showing only the greeting. */
  public newChat(): void {
    this.pendingRequest?.unsubscribe();
    this.messages.set([ASSISTANT_GREETING]);
    this.activeId.set(null);
    this.input.set('');
    this.typing.set(false);
    this.view.set('chat');
    this.menuOpen.set(false);
  }

  public onInput(event: Event): void {
    const element = event.target as HTMLTextAreaElement;
    this.input.set(element.value);
    this.adjustTextareaHeight(element);
  }

  public onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    if (!value) this.expandedCategory.set(this.firstHelpCategory);
  }

  public onKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.ask(this.input());
    }
  }

  /** Grows the input to fit its content, up to the CSS max-height cap where it scrolls instead. */
  private adjustTextareaHeight(element: HTMLTextAreaElement): void {
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }

  public send(): void {
    this.ask(this.input());
  }

  /** Send a question to the assistant API and append the reply when it returns. */
  public ask(question: string): void {
    // Hard stop: a disabled composer should already prevent this, but guard the
    // send path so Enter/Send can never fire a second concurrent request.
    if (this.typing()) return;
    const text = question?.trim().slice(0, this.maxInputLength);
    if (!text) return;

    const id = this.activeId() ?? `c${Date.now()}`;
    this.messages.set([...this.messages(), { role: 'user', text }]);
    this.input.set('');
    this.typing.set(true);
    this.activeId.set(id);

    this.pendingRequest?.unsubscribe();
    this.pendingRequest = this.assistantService.sendMessage(id, text).subscribe({
      next: (response) => {
        // turnId is present as soon as the backend echoes it back (omitted only if
        // its history write failed), so feedback buttons can render on a live reply
        // without waiting for the conversation to be reopened from history.
        const bot: AssistantMessage = response.turnId !== undefined ? { role: 'bot', text: response.reply, turnId: response.turnId, rating: null } : { role: 'bot', text: response.reply };
        this.messages.set([...this.messages(), bot]);
        this.typing.set(false);
        // Return focus to the composer so keyboard users stay in the input; only
        // when the panel is open and showing the chat view (the textarea exists).
        if (this.open() && this.chatView()) setTimeout(() => this.chatInput()?.nativeElement.focus());
      },
      error: () => {
        this.messages.set([...this.messages(), { role: 'bot', text: ASSISTANT_ERROR_MESSAGE }]);
        this.typing.set(false);
        // Return focus to the composer so keyboard users stay in the input; only
        // when the panel is open and showing the chat view (the textarea exists).
        if (this.open() && this.chatView()) setTimeout(() => this.chatInput()?.nativeElement.focus());
      },
    });

    // Track prompt submission in Gainsight PX (fires for every submission path — Enter, send button).
    const windowObj: any = window;
    if (typeof windowObj.aptrinsic === 'function') {
      windowObj.aptrinsic('track', 'Prompt Submitted');
    }
  }

  /**
   * Populate a predefined question into the composer without sending it, so the
   * user can edit, append to, or completely replace it before submitting via
   * Send/Enter. Replaces the current draft (last click wins). No-op while a reply
   * is in flight (the trigger controls are disabled then; this is the guard).
   */
  public populateInput(question: string): void {
    if (this.typing()) return;
    this.view.set('chat');
    this.input.set(question);
    // Focus + caret-to-end + height re-fit run after the [value] binding lands on
    // the next tick. The input-cleared height-reset effect does not fire here
    // (input is non-empty), so re-fit the height explicitly.
    setTimeout(() => {
      const element = this.chatInput()?.nativeElement;
      if (!element) return;
      element.focus();
      const end = element.value.length;
      element.setSelectionRange(end, end);
      this.adjustTextareaHeight(element);
    });
  }

  /** Populate an example question from the help view into the composer (does not send). */
  public askFromHelp(question: string): void {
    this.populateInput(question);
  }

  /** Load a saved conversation's transcript from the backend into the chat view. */
  public openConversation(conversationId: string): void {
    this.pendingRequest?.unsubscribe();
    this.pendingOpenRequest?.unsubscribe();
    this.openConversationError.set(false);
    this.pendingOpenRequest = this.assistantService.getConversation(conversationId).subscribe({
      next: (detail) => {
        this.messages.set(this.toMessages(detail.turns));
        this.feedbackErrorTurnId.set(null);
        this.activeId.set(conversationId);
        this.view.set('chat');
        this.typing.set(false);
        this.input.set('');
      },
      error: () => {
        this.openConversationError.set(true);
      },
    });
  }

  /** Delete a saved conversation; reset the transcript if it was active. */
  public removeConversation(conversationId: string, event: MouseEvent): void {
    event.stopPropagation();
    this.deleteConversationError.set(false);
    this.assistantService.deleteConversation(conversationId).subscribe({
      next: () => {
        this.conversations.set(this.conversations().filter((entry) => entry.conversationId !== conversationId));
        this.historyTotalCount.update((count) => Math.max(0, count - 1));
        if (conversationId === this.activeId()) {
          this.pendingRequest?.unsubscribe();
          this.messages.set([ASSISTANT_GREETING]);
          this.activeId.set(null);
          this.input.set('');
          this.typing.set(false);
          this.view.set('chat');
        }
      },
      error: () => {
        this.deleteConversationError.set(true);
      },
    });
  }

  /**
   * Submit or clear feedback for a bot message that has a turnId (i.e., was
   * loaded from a saved conversation). Clicking the already-active thumb
   * clears the rating; clicking the other one switches it.
   */
  public rateMessage(message: AssistantMessage, rating: 'up' | 'down'): void {
    if (message.turnId === undefined) return;
    this.feedbackErrorTurnId.set(null);
    const nextRating: 'up' | 'down' | null = message.rating === rating ? null : rating;
    this.assistantService.submitFeedback(message.turnId, nextRating).subscribe({
      next: (result) => {
        this.messages.set(this.messages().map((entry) => (entry.turnId === message.turnId ? { ...entry, rating: result.rating } : entry)));
      },
      error: () => {
        this.feedbackErrorTurnId.set(message.turnId ?? null);
      },
    });
  }

  /** Fetch the next page of history and append it to the visible list. */
  public loadMoreHistory(): void {
    this.loadHistoryPage(this.historyPageNum + 1);
  }

  private loadHistoryPage(page: number): void {
    this.historyLoading.set(true);
    if (page === 1) this.historyRefreshing.set(true);
    this.historyError.set(false);
    this.pendingHistoryRequest?.unsubscribe();
    this.pendingHistoryRequest = this.assistantService.listConversations(page, ASSISTANT_HISTORY_PAGE_SIZE).subscribe({
      next: (result) => {
        this.conversations.set(page === 1 ? result.items : [...this.conversations(), ...result.items]);
        this.historyTotalCount.set(result.page.total_count);
        this.historyPageNum = page;
        this.historyLoading.set(false);
        this.historyRefreshing.set(false);
      },
      error: () => {
        this.historyError.set(true);
        this.historyLoading.set(false);
        this.historyRefreshing.set(false);
      },
    });
  }

  /** Map backend turns (request + response pairs) into the chat view's message list. */
  private toMessages(turns: ConversationTurn[]): AssistantMessage[] {
    return turns.flatMap((turn) => [
      { role: 'user' as const, text: turn.requestMessage },
      { role: 'bot' as const, text: turn.responseMessage, turnId: turn.id, rating: turn.rating },
    ]);
  }

  public formatTime(timestamp: number | string): string {
    return this.assistantService.formatTimestamp(timestamp);
  }

  /** A history row's title: the conversation's first user message, truncated — matches the previous localStorage-based history titles. */
  public conversationTitle(conversation: ConversationSummary): string {
    const text = conversation.firstUserMessage;
    return text.length > ASSISTANT_HISTORY_TITLE_MAX_LENGTH ? `${text.slice(0, ASSISTANT_HISTORY_TITLE_MAX_LENGTH)}…` : text;
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.menuOpen()) {
      this.menuOpen.set(false);
      this.moreButton()?.nativeElement.focus();
      return;
    }
    if (this.open()) this.closeChat();
  }

  public ngOnDestroy(): void {
    this.pendingRequest?.unsubscribe();
    this.pendingHistoryRequest?.unsubscribe();
    this.pendingOpenRequest?.unsubscribe();
  }
}
